import { NavLink } from "react-router";
import { useEffect, useState } from "react";
import { api, type ConfigStatus } from "../../api/client";
import { useGitStore } from "../../stores/useGitStore";

const navItems = [
  { to: "/", label: "dashboard" },
  { to: "/templates", label: "templates" },
  { to: "/snippets", label: "snippets" },
  { to: "/live", label: "live" },
  { to: "/sell-sheets", label: "sell sheets" },
  { to: "/audit", label: "audit" },
  { to: "/settings", label: "settings" },
] as const;

interface IntegrationStatusItem {
  key: string;
  label: string;
  connected: boolean;
}

function integrationItem(
  key: string,
  name: string,
  connected: boolean | undefined,
): IntegrationStatusItem | null {
  if (connected === undefined) return null;
  return {
    key,
    label: `${name} online`,
    connected,
  };
}

export function Sidebar() {
  const gitStatus = useGitStore((s) => s.status);
  const fetchStatus = useGitStore((s) => s.fetchStatus);
  const [integrations, setIntegrations] = useState<ConfigStatus | null>(null);
  const [sanityOnline, setSanityOnline] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    api.config
      .status()
      .then(setIntegrations)
      .catch(() => setIntegrations(null));
  }, []);

  useEffect(() => {
    api.sanity
      .families()
      .then(() => setSanityOnline(true))
      .catch(() => setSanityOnline(false));
  }, []);

  const statusItems = [
    integrationItem("git", "git", gitStatus?.connected),
    integrationItem("sanity", "sanity", sanityOnline),
    integrationItem("notion", "notion", integrations?.notion),
  ].filter((item): item is IntegrationStatusItem => item !== null);

  return (
    <aside style={styles.sidebar}>
      <nav style={styles.nav}>
        {navItems.map(({ to, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            style={({ isActive }) => ({
              ...styles.link,
              ...(isActive ? styles.linkActive : {}),
            })}
          >
            {label}
          </NavLink>
        ))}
      </nav>

      {statusItems.length > 0 && (
        <div style={styles.statusBlock}>
          {statusItems.map((item) => (
            <div
              key={item.key}
              style={{
                ...styles.statusLine,
                ...(item.connected ? {} : styles.statusOffline),
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}

const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    position: "sticky",
    top: "calc(var(--topnav-height) + var(--utilbar-height))",
    alignSelf: "flex-start",
    height: "calc(100vh - var(--topnav-height) - var(--utilbar-height))",
    width: "var(--sidebar-width)",
    flexShrink: 0,
    background: "var(--comp-paper)",
    borderRight: "1px solid var(--comp-rule)",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  statusBlock: {
    marginTop: "auto",
    padding: "8px var(--space-3)",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  statusLine: {
    fontSize: 10,
    color: "var(--fg-mute)",
    fontFamily: "var(--font-mono)",
    lineHeight: 1.35,
  },
  statusOffline: {
    textDecoration: "line-through",
  },
  nav: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    padding: "var(--space-2) var(--space-2)",
  },
  link: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    fontWeight: 400,
    color: "var(--fg-2)",
    padding: "4px var(--space-3)",
    borderRadius: "var(--radius-sm)",
    transition: "background 0.15s ease, color 0.15s ease",
  },
  linkActive: {
    color: "var(--fg-1)",
    fontWeight: 600,
    background: "var(--comp-hover)",
  },
};
